import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { User } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * UserCreditsWidget
 *
 * Displays the current user's profile picture surrounded by a circular
 * ag-green progress ring that reflects how many credits they have remaining,
 * alongside an "Upgrade" button that links to the pricing page.
 *
 * Props:
 *   creditsMax  {number}  – total credits for a full ring  (default: 200)
 *   upgradeTo   {string}  – route for the upgrade link     (default: '/pricing')
 */
const UserCreditsWidget = memo(({ creditsMax = 200, upgradeTo = '/manage-subscription' }) => {
  const { user } = useAuth();

  // Use real credits from user object (default 0 if not loaded yet)
  const credits = user?.credits ?? 0;
  const percentage = Math.min(Math.max(credits / creditsMax, 0), 1);

  // SVG ring geometry
  const size = 44;
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - percentage);

  const userPlan = user?.plan || 'free';
  const planLabels = { free: 'Starter', standard: 'Standard', pro: 'Pro' };
  const planLabel = planLabels[userPlan] || 'Starter';
  const canUpgrade = userPlan !== 'pro';

  // Profile photo — only use if it's a real URL (not the default placeholder string)
  const photoUrl =
    user?.photo && user.photo !== 'default.jpg' ? user.photo : null;

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : null;

  return (
    <div className="flex flex-col items-end gap-1 sm:gap-1.5 mt-4 mr-12">
      {/* Avatar ring + Upgrade button */}
      <div className="flex items-center gap-1.5 sm:gap-2">
        {/* Circular progress ring around avatar */}
        <div className="relative flex-shrink-0 w-9 h-9 sm:w-11 sm:h-11">
          <svg
            width={size}
            height={size}
            className="absolute inset-0 -rotate-90 w-full h-full"
            viewBox={`0 0 ${size} ${size}`}
            aria-hidden="true"
          >
            {/* Track */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="#2B2B2B"
              strokeWidth={strokeWidth}
            />
            {/* Progress */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="#C6E982"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              style={{ transition: 'stroke-dashoffset 0.5s ease' }}
            />
          </svg>

          {/* Avatar inner circle */}
          <div
            className="absolute rounded-full overflow-hidden bg-ag-black-600 flex items-center justify-center"
            style={{ inset: strokeWidth + 2 }}
          >
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={user?.name || 'User'}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Hide broken image and fall through to placeholder
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}

            {/* Fallback: initials or User icon */}
            <span
              className="w-full h-full flex items-center justify-center"
              style={{ display: photoUrl ? 'none' : 'flex' }}
            >
              {initials ? (
                <span className="text-xs font-semibold text-ag-green select-none leading-none">
                  {initials}
                </span>
              ) : (
                <User size={16} className="text-ag-green" />
              )}
            </span>
          </div>
        </div>

        {/* Plan badge / Upgrade CTA */}
        {canUpgrade ? (
          <Link
            to={upgradeTo}
            className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg bg-ag-black-800/80 backdrop-blur-sm border border-ag-green/60 text-ag-green text-xs sm:text-sm font-semibold hover:bg-ag-green hover:text-ag-black-800 transition-colors duration-150 whitespace-nowrap"
          >
            Upgrade
          </Link>
        ) : (
          <span className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg bg-ag-green/10 backdrop-blur-sm border border-ag-green/60 text-ag-green text-xs sm:text-sm font-semibold whitespace-nowrap">
            {planLabel}
          </span>
        )}
      </div>

      {/* Plan + credits label */}
      <span className="text-[10px] sm:text-xs text-ag-black-300 bg-ag-black-800/80 backdrop-blur-sm rounded-md px-1.5 sm:px-2 py-0.5 border border-ag-black-400/40 whitespace-nowrap">
        {planLabel} &middot; {credits} credits left
      </span>
    </div>
  );
});

UserCreditsWidget.displayName = 'UserCreditsWidget';

export default UserCreditsWidget;
